import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

import { useAuthStore } from "../store/firebase-auth";
import { useSiteKeyDocStore } from "../store/site-key-doc";
import { useActiveChatRoomStore } from "../store/active-chat-room";
import { useUnreadMessageCountsStore } from "../store/unread-message-counts";
import { HOME_URL } from "../urls";
import { DbRead, DbWrite } from "../database";
import { ExistingChatRoom } from "../models/chat_room";
import {
  ChatMessageManager,
  ExistingChatMessage,
  MessageType,
  NewChatMessage,
} from "../models/chat_message";
import { ExistingChatMessageReadStatus } from "../models/chat_message_read_status";
import { useUserDisplayNamesStore } from "../store/user-display-names-map";
import { useSiteKeyUsersStore } from "../store/site-key-users";
import { useUserPermissionsStore } from "../store/user-permissions";
import { useToastMessageStore } from "../store/toast-messages";
import { createToastMessageID } from "../utils";
import { createOrFindChatRoom } from "../utils/chatUtils";
import { ChatRoomType } from "../models/chat_room";
import { ChatRoomManager } from "../models/chat_room";
import { logger } from "../logging";
import { getContextMetadata } from "../utils/chatUtils";

// Components
import ChatRoomsList from "../components/chat/ChatRoomsList";
import ChatRoomView from "../components/chat/ChatRoomView";

export default function FullScreenChat() {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  // Get stores
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const siteKey = useSiteKeyDocStore((state) => state.siteKeyDoc?.id || "");
  const subscribeToUnreadCounts = useUnreadMessageCountsStore(
    (state) => state.subscribeToUnreadCounts,
  );

  // Get the active chat room ID from the store
  const activeChatRoomId = useActiveChatRoomStore(
    (state) => state.activeChatRoomId,
  );

  //   // Subscribe to unread counts
  //   useEffect(() => {
  //     if (!firebaseUser) return;

  //     const unsubscribe = subscribeToUnreadCounts(siteKey, firebaseUser.uid);
  //     return () => unsubscribe();
  //   }, [siteKey, firebaseUser, subscribeToUnreadCounts]);

  if (!siteKey) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      style={{
        height: "calc(100vh - 8rem)",
        maxHeight: "calc(100vh - 8rem)",
        overflow: "hidden",
      }}
    >
      {/* Main content */}
      <div className="flex flex-1 overflow-hidden" ref={contentRef}>
        {/* Load the existing ChatRooms component which will be adapted for full-screen mode */}
        <FullScreenChatAdapter
          siteKey={siteKey}
          initialRoomId={activeChatRoomId}
        />
      </div>
    </div>
  );
}

// Interface for the adapter component
interface FullScreenChatAdapterProps {
  siteKey: string;
  initialRoomId: string | null;
}

// This adapter component transforms the ChatRooms component to fit our new layout
function FullScreenChatAdapter({
  siteKey,
  initialRoomId,
}: FullScreenChatAdapterProps) {
  const navigate = useNavigate();
  const [selectedRoomID, setSelectedRoomID] = useState<string | null>(
    initialRoomId,
  );
  const [myChatRooms, setMyChatRooms] = useState<ExistingChatRoom[]>([]);
  const [messagesForRoom, setMessagesForRoom] = useState<
    ExistingChatMessage[] | null
  >(null);
  const [unreadMessageStatuses, setUnreadMessageStatuses] = useState<
    ExistingChatMessageReadStatus[]
  >([]);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: { progress: number; file: File };
  }>({});

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const userDisplayNamesMap = useUserDisplayNamesStore(
    (state) => state.userDisplayNames,
  );
  const siteKeyUsersList = useSiteKeyUsersStore(
    (state) => state.siteKeyUsersList,
  );
  const unreadCounts = useUnreadMessageCountsStore(
    (state) => state.unreadCounts,
  );
  const setActiveChatRoomId = useActiveChatRoomStore(
    (state) => state.setActiveChatRoomId,
  );
  const clearActiveChatRoom = useActiveChatRoomStore(
    (state) => state.clearActiveChatRoom,
  );
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );

  // Update unreadMessageStatuses when store changes
  useEffect(() => {
    setUnreadMessageStatuses(unreadCounts);
  }, [unreadCounts]);

  // Keep active chat room ID in sync with selectedRoomID
  useEffect(() => {
    if (selectedRoomID) {
      setActiveChatRoomId(selectedRoomID);
    } else if (initialRoomId !== selectedRoomID) {
      setActiveChatRoomId(null);
    }
  }, [selectedRoomID, setActiveChatRoomId, initialRoomId]);

  // Subscribe to chat rooms
  useEffect(() => {
    function getMyChatRooms() {
      if (!firebaseUser) return undefined;

      return DbRead.chatRooms.subscribeByUserID({
        siteKey,
        userID: firebaseUser.uid,
        onChange: (rooms) => {
          try {
            // Filter out any invalid rooms before setting state
            const validRooms = rooms.filter((room) => {
              try {
                return (
                  room && Array.isArray(room.userIds) && room.userIds.length > 0
                );
              } catch (error) {
                console.warn("Invalid chat room filtered out", error);
                return false;
              }
            });
            setMyChatRooms(validRooms);
          } catch (error) {
            console.error("Error processing chat rooms", error);
            setMyChatRooms([]);
          }
        },
        onError: (error) => {
          console.error("Error fetching chat rooms", error);
          setMyChatRooms([]);
        },
      });
    }

    const unsubscribePromise = getMyChatRooms();
    return () => unsubscribePromise && unsubscribePromise();
  }, [firebaseUser, siteKey]);

  // Subscribe to chat messages for the selected room
  useEffect(() => {
    function getChatMessages() {
      if (!firebaseUser) return undefined;
      if (!selectedRoomID) {
        setMessagesForRoom(null);
        return undefined;
      }

      return DbRead.chatMessages.subscribeByRoomID({
        siteKey,
        roomID: selectedRoomID,
        onChange: (messages) => {
          try {
            const validMessages = messages.filter((message) => {
              try {
                return (
                  message &&
                  typeof message.createdBy === "string" &&
                  (typeof message.text === "string" ||
                    message.type === MessageType.IMAGE)
                );
              } catch (error) {
                console.warn("Invalid chat message filtered out", error);
                return false;
              }
            });
            setMessagesForRoom(validMessages);
          } catch (error) {
            console.error("Error processing chat messages", error);
            setMessagesForRoom(null);
          }
        },
        onError: (error) => {
          console.error("Error fetching chat messages", error);
          setMessagesForRoom(null);
        },
      });
    }

    const unsubscribePromise = getChatMessages();
    return () => unsubscribePromise && unsubscribePromise();
  }, [selectedRoomID, firebaseUser, siteKey]);

  // Function to add a message to the current chat
  async function handleAddMessage(message: string) {
    if (!firebaseUser || !selectedRoomID || message.trim() === "") {
      return;
    }

    try {
      // Get the context metadata from user's recent actions
      const contextMetadata = getContextMetadata();

      // Create a new message with the context metadata
      const newMessage: NewChatMessage = {
        text: message,
        metadata: contextMetadata,
      };

      // Send the message
      await DbWrite.chatMessage.add(
        siteKey,
        selectedRoomID,
        ChatMessageManager.createFromNewMessage(
          newMessage,
          firebaseUser.uid,
          selectedRoomID,
          MessageType.TEXT,
        ),
      );
    } catch (error) {
      console.warn("Error adding message", error);
    }
  }

  // Function to handle image uploads
  async function handleImageUpload(file: File) {
    if (!firebaseUser || !selectedRoomID) {
      return;
    }

    try {
      // Generate a unique ID for the image
      const imageId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Add to uploading images state
      setUploadingImages((prev) => ({
        ...prev,
        [imageId]: { progress: 0, file },
      }));

      // Get the storage reference
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `chat_images/${siteKey}/${selectedRoomID}/${imageId}`,
      );

      // Upload the image
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Listen for state changes, errors, and completion of the upload
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Get upload progress
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          // Update progress in state
          setUploadingImages((prev) => ({
            ...prev,
            [imageId]: { ...prev[imageId], progress },
          }));
        },
        (error) => {
          // Handle unsuccessful uploads
          console.warn("Error uploading image", error);

          // Remove from uploading images
          setUploadingImages((prev) => {
            const newState = { ...prev };
            delete newState[imageId];
            return newState;
          });
        },
        async () => {
          try {
            // Upload completed successfully, get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Get image dimensions
            const dimensions = await getImageDimensions(file);

            if (!dimensions) {
              console.error("Could not get image dimensions");
              return;
            }

            // Get the context metadata from user's recent actions
            const contextMetadata = getContextMetadata();

            // Create a new message with the image details and context metadata
            const newMessage: NewChatMessage = {
              uri: downloadURL,
              name: file.name,
              size: file.size,
              height: dimensions.height,
              width: dimensions.width,
              mimeType: file.type,
              metadata: contextMetadata,
            };

            // Send the message
            await DbWrite.chatMessage.add(
              siteKey,
              selectedRoomID,
              ChatMessageManager.createFromNewMessage(
                newMessage,
                firebaseUser.uid,
                selectedRoomID,
                MessageType.IMAGE,
              ),
            );
            // Remove from uploading images
            setUploadingImages((prev) => {
              const newState = { ...prev };
              delete newState[imageId];
              return newState;
            });
          } catch (error) {
            console.warn("Error processing uploaded image", error);

            // Remove from uploading images
            setUploadingImages((prev) => {
              const newState = { ...prev };
              delete newState[imageId];
              return newState;
            });
          }
        },
      );
    } catch (error) {
      console.warn("Error starting image upload", error);
    }
  }

  // Helper function to get image dimensions
  function getImageDimensions(
    file: File,
  ): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve, reject) => {
      const img = new globalThis.Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = URL.createObjectURL(file);
    });
  }

  // Function to start a new chat
  async function startNewChat(selectedUserIds: string[], groupName?: string) {
    await createOrFindChatRoom({
      firebaseUser,
      selectedUserIds,
      myChatRooms,
      userDisplayNamesMap,
      siteKey,
      groupName,
      canCreateGroupChats: userPermissions?.permissions.isSiteAdmin === true,
      onSuccess: (chatRoomId) => {
        setSelectedRoomID(chatRoomId);
      },
      onError: (message) => {
        addToastMessage({
          id: createToastMessageID(),
          message,
          type: "error",
          dialog: false,
        });
      },
    });
  }

  // Find the selected room
  const selectedRoom = selectedRoomID
    ? myChatRooms.find((room) => room.id === selectedRoomID)
    : null;

  // Function to start a Stilton AI chat
  async function startStiltonChat() {
    try {
      if (!firebaseUser) return;

      // Check if a Stilton chat already exists
      const existingStiltonChat = myChatRooms.find(
        (room) => room.type === ChatRoomType.STILTON,
      );

      if (existingStiltonChat) {
        // If it exists, just navigate to it
        setSelectedRoomID(existingStiltonChat.id);
        setActiveChatRoomId(existingStiltonChat.id);
        return;
      }

      // Create a new Stilton chat room
      const stiltonChatRoom = ChatRoomManager.createStiltonChatRoom(
        firebaseUser.uid,
        firebaseUser.uid,
      );

      // Add the chat room to the database
      const newRoomRef = await DbWrite.chatRoom.add(siteKey, stiltonChatRoom);

      // Navigate to the new room
      setSelectedRoomID(newRoomRef.id);
      setActiveChatRoomId(newRoomRef.id);
    } catch (error) {
      logger.error("Error creating Stilton chat:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Failed to create Stilton chat",
        type: "error",
        dialog: false,
      });
    }
  }

  return (
    <>
      {/* Left sidebar with chat list */}
      <div className="flex w-1/3 flex-col overflow-hidden border-r border-gray-200 bg-white">
        <div className="flex-1 overflow-y-auto">
          <ChatRoomsList
            chatRooms={myChatRooms}
            firebaseUser={firebaseUser}
            userDisplayNamesMap={userDisplayNamesMap}
            siteKeyUsersList={siteKeyUsersList}
            onSelectRoom={(chatRoomID) => {
              setSelectedRoomID(chatRoomID);
            }}
            onStartNewChat={startNewChat}
            onStartStiltonChat={startStiltonChat}
            unreadMessageStatuses={unreadMessageStatuses}
          />
        </div>
      </div>

      {/* Right content area with selected chat or placeholder */}
      <div className="flex flex-1 flex-col overflow-hidden bg-white pl-2">
        {selectedRoom && messagesForRoom !== null ? (
          <div className="flex h-full flex-col overflow-hidden">
            <ChatRoomView
              room={selectedRoom}
              messages={messagesForRoom}
              siteKey={siteKey}
              firebaseUser={firebaseUser}
              userDisplayNamesMap={userDisplayNamesMap}
              siteKeyUsersList={siteKeyUsersList}
              onBackClick={() => {
                clearActiveChatRoom();
                setSelectedRoomID(null);
              }}
              uploadingImages={uploadingImages}
              handleImageUpload={handleImageUpload}
              handleAddMessage={handleAddMessage}
              markRoomAsRead={() => {
                if (firebaseUser && selectedRoomID) {
                  DbWrite.chatMessageReadStatus
                    .markRoomAsRead(siteKey, firebaseUser.uid, selectedRoomID)
                    .catch((error) => {
                      console.error("Error marking room as read", error);
                    });
                }
              }}
              unreadMessageStatuses={unreadMessageStatuses}
            />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-gray-500">
              <p className="text-lg">
                Select a conversation or start a new chat
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
